import FileInputUploadFieldNew from 'components/styledFormElements/FineInputUploadNew'
import InputField from 'components/styledFormElements/InputField'
import React from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useObjectUrl } from 'hooks/useObjectUrl'
import { getImageSrc } from 'services/util'

type Props = {
    prefix: 'first_time_user_header' | 'second_time_user_header' | 'discovery_search_header' | 'voice_assistant_header'
    title?: string
}

const HEADER_HELP_TEXTS = {
    first_time_user_header: {
        imageContext: 'first-time search screen',
        heading: 'Enter the main title shown on the first-time search screen.',
        description: 'Add a short supporting description displayed below the first-time search header.',
    },
    second_time_user_header: {
        imageContext: 'returning-user search screen',
        heading: 'Enter the main title shown on the returning-user search screen.',
        description: 'Add a short supporting description displayed below the returning-user search header.',
    },
    discovery_search_header: {
        imageContext: 'discovery search screen',
        heading: 'Enter the main title shown on the discovery search screen.',
        description: 'Add a short supporting description displayed below the discovery search header.',
    },
    voice_assistant_header: {
        imageContext: 'voice assistant screen',
        heading: 'Enter the main title shown on the voice assistant screen.',
        description: 'Add a short supporting description displayed below the voice assistant header.',
    },
} as const

const UserHeaderConfig = ({ prefix, title }: Props) => {
    const {
        control,
        register,
        formState: { errors },
    } = useFormContext<any>()

    const watchedImage = useWatch({ control, name: `${prefix}.image` })
    const imagePreview = useObjectUrl(watchedImage)

    const helpText = HEADER_HELP_TEXTS[prefix]

    return (
        <div className="space-y-4 flex-1 flex flex-col">
            <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                {title}
            </h5>

            <div className="grid grid-cols-1 gap-6 flex-1">
                {/* Image Field */}
                <FileInputUploadFieldNew
                    label="Image"
                    name={`${prefix}.image`}
                    control={control}
                    errors={errors}
                    preview={getImageSrc(imagePreview)}
                    allowedTypes={[
                        'image/jpeg',
                        'image/png',
                        'image/jpg',   
                        'image/gif',
                        'image/webp'
                    ]}
                    required
                    rules={{
                        required: 'Image is required',
                    }}
                    tooltip={{
                        title: 'Image requirements',
                        items: [
                            `Upload the image displayed at the top of the ${helpText.imageContext}`,
                            'JPG, PNG, WEBP, GIF, JPEG',
                            '≤ 1 MB',
                        ],
                        position: "top"
                    }}
                />

                {/* Heading Field */}
                <InputField
                    label="Heading"
                    type="text"
                    required
                    helpText={helpText.heading}
                    maxLength={100}
                    register={register(`${prefix}.heading`, {
                        required: 'Heading is required',
                        maxLength: {
                            value: 100,
                            message: 'Heading must be 100 characters or less'
                        }
                    })}
                    error={(errors[prefix] as any)?.heading?.message as string}
                />

                {/* Description Field */}
                <InputField
                    label="Description"
                    isTextarea
                    row={2}
                    required
                    helpText={helpText.description}
                    maxLength={500}
                    register={register(`${prefix}.description`, {
                        required: 'Description is required',
                        maxLength: {
                            value: 500,
                            message: 'Description must be 500 characters or less'
                        }
                    })}
                    error={(errors[prefix] as any)?.description?.message as string}
                />
                {prefix === 'voice_assistant_header' && (
                    <>
                        {/* CTA Label */}
                        <InputField
                            label="CTA Label"
                            type="text"
                            required
                            helpText="Label for the call-to-action button shown on the Voice Assistant header."
                            maxLength={50}
                            register={register(`${prefix}.cta_label`, {
                                required: 'CTA Label is required',
                                maxLength: {
                                    value: 100,
                                    message: 'CTA Label must be 100 characters or less',
                                },
                            })}
                            error={(errors[prefix] as any)?.cta_label?.message as string}
                        />
                    </>
                )}
            </div>
        </div>
    )
}

export default UserHeaderConfig
